<script setup lang="ts">
  import { reactiveOmit } from '@vueuse/core';
  import type { TabsTriggerProps } from 'reka-ui';
  import { TabsTrigger, useForwardProps } from 'reka-ui';
  import { inject } from 'vue';
  import type { HTMLAttributes } from 'vue';
  import { useRouter } from 'vue-router';
  import type { RouteLocationRaw } from 'vue-router';

  import { cn } from '@ui/utils/utils';

  export type RouteStrategy = 'replace' | 'push';

  const props = withDefaults(
    defineProps<
      TabsTriggerProps & {
        class?: HTMLAttributes['class'];
        routeStrategy?: RouteStrategy;
        routeToValue?: boolean;
        to?: RouteLocationRaw;
        query?: Record<string, any>;
      }
    >(),
    {
      routeStrategy: 'replace',
    },
  );

  const delegatedProps = reactiveOmit(props, 'class');

  const forwardedProps = useForwardProps(delegatedProps);

  const context = inject<{ variant: 'glass' | 'underline' | 'panel' }>('tabs', {
    variant: 'underline',
  });

  const baseClasses =
    "not-data-[state=active]:hover:opacity-70 z-4 data-[state=active]:text-foreground focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:outline-ring text-muted-foreground inline-flex h-[calc(100%-1px)] flex-1 items-center justify-center gap-1.5 rounded-sm border border-transparent px-2 py-1 text-xs font-medium whitespace-nowrap transition-[color,box-shadow] focus-visible:ring-[3px] focus-visible:outline-1 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4";

  const panelClasses =
    "flex-1 h-full px-4 inline-flex items-center justify-center gap-1.5 text-sm font-medium whitespace-nowrap text-muted-foreground hover:bg-muted/50 data-[state=active]:bg-muted data-[state=active]:text-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4";

  const router = useRouter();

  const onClick = () => {
    if (props.to || props.routeToValue) {
      router[props.routeStrategy](
        props.to || {
          name: props.value as string,
          query: props.query,
        },
      );
    }
  };
</script>

<template>
  <TabsTrigger
    data-slot="tabs-trigger"
    :class="cn(context.variant === 'panel' ? panelClasses : baseClasses, props.class)"
    v-bind="forwardedProps"
    @click="onClick"
  >
    <slot />
  </TabsTrigger>
</template>
